import {
  AloftArea,
  AloftPrice,
  AloftPriceJSON,
  EXPLICIT_UNKNOWN_SOURCE_DISPLAY,
  Improvements,
  LatitudeLongitude,
  ManualAdjustments,
  ManufacturedHomeFeatures,
  ManufacturedHomeFoundationDisplay,
  ORDER_FEATURES,
  OrderFeature,
  OrderFeatureDefinition,
  SelectedSources,
  SubjectData,
  isExplicitUnknownSelectedForFieldPath,
} from '@aloft/toolkit-shared';

import { makeStreetViewStaticURL } from '../../../hooks/googleMaps/makeStreetViewStaticURL.ts';
import { proximityMiles } from '../../../utils/proximityMiles.ts';
import { FeatureArea, SalesComparisonAdjustmentKey } from '../types.ts';

export const getSubjectPropertyImageUrl = (subject?: SubjectData): string | undefined =>
  subject?.readonlyData.backupListingData?.listing.Media?.[0]?.MediaURL;

export const formatFeatureArea = ({ area, unit, type, hasFeature }: FeatureArea) => {
  if (!area || !unit || !hasFeature) return '';
  const aloftArea = AloftArea.fromNumber(area, unit).toString(true);
  return type ? `${aloftArea} ${type}` : aloftArea;
};

export const formatFeatureAreas = (areas?: FeatureArea[]) => {
  if (!areas || areas.length === 0) return '';
  return areas
    .map((area) => formatFeatureArea(area))
    .filter(Boolean)
    .join('; ');
};

export const resolveOrderKeyedFeaturePdfCell = (
  improvements: Improvements,
  orderFeatures: OrderFeature[] | undefined,
  params: {
    orderKeys: readonly OrderFeature[];
    featureDefs: readonly OrderFeatureDefinition[];
    format: () => string;
    unknownEarly?: () => string | undefined;
  },
): string => {
  const unknownLabel = params.unknownEarly?.();
  if (unknownLabel) return unknownLabel;
  const formatted = params.format();
  if (formatted) return formatted;
  if (params.featureDefs.some((d) => d.isNone(improvements))) return 'None';
  if (orderFeatures === undefined) return '';
  if (params.orderKeys.every((k) => !orderFeatures.includes(k))) return '';
  if (params.featureDefs.some((d) => d.isPresent(improvements))) return '';
  return 'None';
};

export type SingleAreaOrderFeature = 'attic' | 'shed' | 'barn' | 'adu' | 'workshop';

export type ImprovementsPathPrefix = 'editableData.improvements' | 'editableData.arv.improvements';

export const resolveOrderKeyedAreaFeaturePdfCell = (
  improvements: Improvements,
  orderFeatures: OrderFeature[] | undefined,
  feature: SingleAreaOrderFeature,
  selectedSources?: SelectedSources,
  improvementsPathPrefix: ImprovementsPathPrefix = 'editableData.improvements',
): string => {
  const block = improvements[feature];
  const path = `${improvementsPathPrefix}.${feature}.area`;
  return resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
    orderKeys: [feature],
    featureDefs: [ORDER_FEATURES[feature]],
    unknownEarly: () =>
      isExplicitUnknownSelectedForFieldPath(selectedSources, path)
        ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
        : undefined,
    format: () =>
      formatFeatureArea({
        area: block?.area?.unitAmount,
        unit: block?.area?.unit,
        hasFeature: block?.hasFeature,
      }),
  });
};

export const getFoundationDisplay = (improvements?: Improvements): string => {
  let foundationLabel: ManufacturedHomeFoundationDisplay | undefined;
  if (improvements?.propertyType === 'manufacturedHome') {
    const { foundation } = improvements as ManufacturedHomeFeatures;
    foundationLabel = foundation as ManufacturedHomeFoundationDisplay;
  }

  return foundationLabel ? ` (${foundationLabel})` : '';
};

export const getDistanceToSubject = (
  to?: Partial<LatitudeLongitude>,
  from?: Partial<LatitudeLongitude>,
): number | undefined => (to && from ? +(proximityMiles(to, from)?.toFixed(2) ?? 0) : undefined);

export const getPropertyImageWithFallback = async (
  mlsPhotoUrl?: string,
  coordinates?: LatitudeLongitude,
): Promise<string | undefined> => {
  if (mlsPhotoUrl) {
    try {
      // Try to fetch the image to check if it's accessible
      const response = await fetch(mlsPhotoUrl, {
        method: 'HEAD',
        mode: 'cors',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) return mlsPhotoUrl;
    } catch {
      if (coordinates) return makeStreetViewStaticURL(coordinates);
    }
  }

  // If no MLS photo, try Street View
  if (coordinates) return makeStreetViewStaticURL(coordinates);
  return undefined;
};

const manualAdjustmentsToGridAdjustmentsMap: {
  [p in keyof ManualAdjustments]: SalesComparisonAdjustmentKey[];
} = {
  dateOfSale: ['saleDateAdjustment'],
  site: ['siteSizeAdjustment'],
  actualAge: ['actualAgeAdjustment'],
  quality: ['qualityAdjustment'],
  condition: ['conditionAdjustment'],
  gla: ['glaAdjustment'],
  bedrooms: ['bedCountAdjustment'],
  bathAboveGrade: ['fullBathsAdjustment', 'halfBathsAdjustment'],
  fullBaths: ['fullBathsAdjustment'],
  halfBaths: ['halfBathsAdjustment'],
  basement: ['basementTotalAdjustment', 'basementFinishedAdjustment'],
  basementTotal: ['basementTotalAdjustment'],
  basementFinished: ['basementFinishedAdjustment'],
  bathBelowGrade: ['basementFullBathsAdjustment', 'basementHalfBathsAdjustment'],
  basementFullBaths: ['basementFullBathsAdjustment'],
  basementHalfBaths: ['basementHalfBathsAdjustment'],
  garageCarport: ['garageAdjustment'],
  porchPatioDeck: ['porchAdjustment'],
  attic: ['atticAdjustment'],
  fireplace: ['fireplaceAdjustment'],
  poolSpa: ['poolAdjustment'],
  shed: ['shedAdjustment'],
  barn: ['barnAdjustment'],
  adu: ['aduAdjustment'],
  workshop: ['workshopAdjustment'],
  manual: ['manualCostAdjustment'],
};

const isExplicitZero = (value?: AloftPriceJSON) =>
  value && AloftPrice.schema.parse(value).equals(AloftPrice.zero);

export const getExplicitZeroAdjustments = (
  manualAdjustments?: ManualAdjustments & { id?: string },
): Partial<Record<SalesComparisonAdjustmentKey, true>> | undefined => {
  if (!manualAdjustments) return undefined;

  const keys = Object.entries(manualAdjustmentsToGridAdjustmentsMap).flatMap(([key, value]) =>
    isExplicitZero(manualAdjustments[key as keyof ManualAdjustments]) ? value : [],
  );

  if (keys.length === 0) return undefined;
  return Object.fromEntries(keys.map((k) => [k, true]));
};
